import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Tabs, Tab, Button, Grid, Paper, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import SettingsIcon from '@mui/icons-material/Settings';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import BuildIcon from '@mui/icons-material/Build';
import ReceiptIcon from '@mui/icons-material/Receipt';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SmartToyIcon from '@mui/icons-material/SmartToy';

// Dashboard components
import ServiceOfferingsPanel from '../components/dashboard/ServiceOfferingsPanel';
import AppointmentsPanel from '../components/dashboard/AppointmentsPanel';
import AISettingsPanel from '../components/dashboard/AISettingsPanel';
import WhatsAppSettingsPanel from '../components/dashboard/WhatsAppSettingsPanel';
import AvailabilityPanel from '../components/dashboard/AvailabilityPanel';
import QuotesPanel from '../components/dashboard/QuotesPanel';

// API services
import { getProviderSettings, createProviderSettings } from '../services/providerService';

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 'bold',
  fontSize: '1rem',
  marginRight: theme.spacing(1),
  '&.Mui-selected': {
    color: theme.palette.primary.main,
  },
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `dashboard-tab-${index}`,
    'aria-controls': `dashboard-tabpanel-${index}`,
  };
}

export default function ProviderDashboard() {
  const [tabValue, setTabValue] = useState(0);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect if not logged in
    if (!currentUser) {
      navigate('/login');
      return;
    }

    const fetchProviderSettings = async () => {
      try {
        setLoading(true);
        let providerData;
        
        try {
          // Try to get existing settings
          providerData = await getProviderSettings(currentUser.uid);
        } catch (error) {
          // If 404 error (settings don't exist), create default settings
          if (error.response && error.response.status === 404) {
            console.log('Provider settings not found. Creating default settings...');
            
            // Create default provider settings
            const defaultSettings = {
              provider_id: currentUser.uid,
              company_name: 'My Professional Services',
              ai_automation: {
                take_notes: true,
                ask_questions: true,
                generate_quotes: false,
                book_appointments: false,
                send_reminders: true,
                custom_prompt: 'Be professional and courteous when interacting with customers.'
              },
              service_offerings: [],
              availability: [
                {
                  day_of_week: 'Monday',
                  start_time: '09:00',
                  end_time: '17:00'
                },
                {
                  day_of_week: 'Tuesday',
                  start_time: '09:00',
                  end_time: '17:00'
                },
                {
                  day_of_week: 'Wednesday',
                  start_time: '09:00',
                  end_time: '17:00'
                },
                {
                  day_of_week: 'Thursday',
                  start_time: '09:00',
                  end_time: '17:00'
                },
                {
                  day_of_week: 'Friday',
                  start_time: '09:00',
                  end_time: '17:00'
                }
              ],
              whatsapp_settings: {
                enabled: false,
                phone_number: '',
                business_account_id: '',
                verified: false,
                auto_reply_enabled: true,
                auto_reply_message: 'Thank you for contacting us. We will respond to your message as soon as possible.'
              }
            };
            
            // Create settings in the backend
            await createProviderSettings(defaultSettings);
            
            // Fetch the newly created settings
            providerData = await getProviderSettings(currentUser.uid);
          } else {
            // If it's not a 404 error, rethrow
            throw error;
          }
        }
        
        setSettings(providerData);
      } catch (err) {
        console.error('Error fetching or creating provider settings:', err);
        setError('Failed to load your settings. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchProviderSettings();
  }, [currentUser, navigate]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            {error}
          </Typography>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Paper elevation={0} sx={{ mt: 4, mb: 4, p: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Professional Dashboard
          </Typography>
          <Button 
            variant="outlined" 
            startIcon={<SettingsIcon />}
            onClick={() => navigate('/profile')}
          >
            Account Settings
          </Button>
        </Box>

        {settings && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="h5">{settings.company_name}</Typography>
          </Box>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="dashboard tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <StyledTab icon={<BuildIcon />} label="Services" {...a11yProps(0)} />
            <StyledTab icon={<CalendarMonthIcon />} label="Appointments" {...a11yProps(1)} />
            <StyledTab icon={<ReceiptIcon />} label="Quotes" {...a11yProps(2)} />
            <StyledTab icon={<SmartToyIcon />} label="AI Settings" {...a11yProps(3)} />
            <StyledTab icon={<WhatsAppIcon />} label="WhatsApp" {...a11yProps(4)} />
            <StyledTab icon={<CalendarMonthIcon />} label="Availability" {...a11yProps(5)} />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <ServiceOfferingsPanel 
            settings={settings} 
            setSettings={setSettings} 
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <AppointmentsPanel 
            providerId={currentUser?.uid} 
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <QuotesPanel 
            providerId={currentUser?.uid} 
          />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <AISettingsPanel 
            settings={settings} 
            setSettings={setSettings} 
          />
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <WhatsAppSettingsPanel 
            settings={settings} 
            setSettings={setSettings} 
          />
        </TabPanel>

        <TabPanel value={tabValue} index={5}>
          <AvailabilityPanel 
            settings={settings} 
            setSettings={setSettings} 
          />
        </TabPanel>
      </Paper>
    </Container>
  );
}
